/*
 * Wire types come from `@scaleaq/sitecontrol-lib`
 * `SnapshotDescriptor` carries:
 *   path       storage-relative `{cameraId}/{fileName}`
 *   url        API-relative serving URL; make absolute via `useSnapshotFileUrl`
 *   cameraId
 *   tier       standardized 'HIGH' | 'LOW' (typed as string by the generator)
 *   frameAtUtc wall-clock UTC of the captured frame, ISO string, second precision
 */

import { useCallback } from 'react';

import {
  MediaEndpoints,
  type DeleteSnapshotsRequest,
  type SnapshotDescriptor,
  type SnapshotPageResponse,
} from '@scaleaq/sitecontrol-lib';

import { API_NAMES } from '@/constants';
import {
  useAuthApiDelete,
  useAuthApiGetSingleFetch,
  useAuthApiPostFormData,
  useGetApiUrl,
} from '@/dataAccess/rest/apiHooks';

export const SNAPSHOT_CACHE_KEY = 'cameraSnapshots';

/**
 * Newest-first page of {@link SnapshotDescriptor}; `total` is the whole camera count, not
 * just this page (it powers the history slider).
 *
 * ⚠ The generated `SnapshotPageResponse` types the bounds as optional (`string | undefined`)
 * because TypeGen renders `DateTimeOffset?` that way, but MediaServer.Api registers no
 * `JsonIgnoreCondition.WhenWritingNull`, so an empty combo puts a literal `null` on the wire.
 * Narrowed here so truthiness checks stay honest — do not use `=== undefined` or `??` on
 * these two fields.
 */
export type SnapshotPage = Omit<SnapshotPageResponse, 'oldestUtc' | 'newestUtc'> & {
  oldestUtc: string | null;
  newestUtc: string | null;
};

/** Client-side capture input; no server DTO equivalent (multipart form, not JSON). */
export type UploadSnapshotArgs = {
  blob: Blob;
  cameraId: string;
  tier: string;
  frameAt: Date;
};

/** Absolute URL for a {@link SnapshotDescriptor.url} (usable as `<img src>`; GET is unauthenticated). */
export const useSnapshotFileUrl = () => {
  const api = useGetApiUrl(API_NAMES.mediaserverApi);
  return useCallback((relativeUrl: string) => (api ? `${api}${relativeUrl}` : relativeUrl), [api]);
};

/**
 * Upload one captured JPEG. The server dedupes same-second captures for the same
 * combo (first write wins) and returns the existing descriptor in that case.
 */
export const useUploadSnapshot = () =>
  useAuthApiPostFormData<UploadSnapshotArgs, SnapshotDescriptor>({
    baseApiRoute: API_NAMES.mediaserverApi,
    cacheKeys: [SNAPSHOT_CACHE_KEY],
    route: MediaEndpoints.snapshots,
    identifier: 'snapshot',
    returnJsonResult: true,
    toFormData: ({ blob, cameraId, tier, frameAt }) => {
      const form = new FormData();
      form.append('image', blob, 'snapshot.jpg');
      form.append('cameraId', cameraId);
      form.append('tier', tier);
      form.append('frameAtUtc', frameAt.toISOString());
      return form;
    },
  });

/**
 * Delete stored snapshots (batch endpoint; paths are storage-relative
 * {@link SnapshotDescriptor.path}). Invalidates the history cache so the strip/slider
 * reflect the removal immediately.
 */
export const useDeleteSnapshots = () =>
  useAuthApiDelete<DeleteSnapshotsRequest, void>({
    baseApiRoute: API_NAMES.mediaserverApi,
    cacheKeys: [SNAPSHOT_CACHE_KEY],
    route: MediaEndpoints.snapshots,
    identifier: 'snapshots',
  });

type UseSnapshotHistoryArgs = {
  cameraId: string;
  /** Exclusive newest-bound cursor (ISO timestamp); omit for the latest page. */
  before?: string;
  limit?: number;
  enabled?: boolean;
};

/** Newest-first page of stored snapshots for one camera — both tiers mixed together. */
export const useSnapshotHistory = ({
  cameraId,
  before,
  limit = 12,
  enabled = true,
}: UseSnapshotHistoryArgs) => {
  const params = new URLSearchParams({ cameraId, limit: String(limit) });
  if (before) params.set('before', before);

  return useAuthApiGetSingleFetch<SnapshotPage>({
    baseApiRoute: API_NAMES.mediaserverApi,
    route: `${MediaEndpoints.snapshots}?${params.toString()}`,
    cacheKeys: [SNAPSHOT_CACHE_KEY, cameraId, before ?? 'latest', limit],
    returnJsonResult: true,
    enabled: enabled && Boolean(cameraId),
    staleTime: 15_000,
    identifier: 'snapshots',
  });
};
